import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
import FocusLock from 'react-focus-lock';

import { useConsent } from '~/context/ConsentContext';

import Button from '~/components/base/Button';
import BaseLink from '~/components/base/BaseLink';

import useScrollLock from '~/hooks/useScrollLock';

import { isBrowser, hexToRgba } from '~/utilities/helpers';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndex.cookieConsent};
    background-color: ${({ theme }) =>
        hexToRgba(theme.colors.primary.black, 0.6)};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    overflow: auto;
`;

const Modal = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.white};
    border-radius: 8px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 8px 40px
        ${({ theme }) => hexToRgba(theme.colors.primary.black, 0.4)};
`;

const ModalHeader = styled.div`
    padding: 24px 28px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary.lightGray};
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: 2.2rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.colors.primary.darkBlue};
`;

const ModalBody = styled.div`
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Description = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 1.5rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.text.black};
`;

const ToggleRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding: 16px;
    background-color: ${({ theme }) => theme.backgroundColors.lightGray};
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.primary.lightGray};
`;

const ToggleLabelGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ToggleLabel = styled.span`
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    font-size: 1.6rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.colors.primary.darkBlue};
`;

const ToggleDescription = styled.span`
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 1.3rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.text.black};
`;

const AlwaysActive = styled.span`
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 1.3rem;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.accent.blue};
    flex-shrink: 0;
`;

const ToggleTrack = styled.button`
    position: relative;
    width: 52px;
    height: 28px;
    border-radius: 14px;
    border: none;
    padding: 0;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    flex-shrink: 0;
    transition: background-color 200ms ease-in-out;
    background-color: ${({ theme, checked }) =>
        checked ? theme.colors.accent.blue : theme.colors.accent.lightGray};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary.darkBlue};
        outline-offset: 2px;
    }
`;

const ToggleKnob = styled.span`
    position: absolute;
    top: 4px;
    left: ${({ checked }) => (checked ? '28px' : '4px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary.white};
    transition: left 200ms ease-in-out;
`;

const ModalFooter = styled.div`
    padding: 20px 28px 24px;
    border-top: 1px solid ${({ theme }) => theme.colors.primary.lightGray};
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FooterButtons = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;

    /* Keep all three actions on one row, even on the narrowest phones. */
    > button {
        flex: 1 1 0;
        min-width: 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoint.largePhoneMax}) {
        gap: 8px;

        > button {
            font-size: 1.3rem;
            padding: 0.55em 0.5em;
            line-height: 1.15;
        }
    }
`;

const PolicyLink = styled(BaseLink)`
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: 1.4rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.colors.primary.darkBlue};
    text-decoration: underline;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.accent.blue};
    }
`;

const Toggle = ({ label, description, checked, disabled, onToggle }) => (
    <ToggleRow>
        <ToggleLabelGroup>
            <ToggleLabel>{label}</ToggleLabel>
            {description && (
                <ToggleDescription>{description}</ToggleDescription>
            )}
        </ToggleLabelGroup>
        {disabled ? (
            <AlwaysActive>Always active</AlwaysActive>
        ) : (
            <ToggleTrack
                type="button"
                checked={checked}
                onClick={onToggle}
                role="switch"
                aria-checked={checked}
                aria-label={label}
            >
                <ToggleKnob checked={checked} />
            </ToggleTrack>
        )}
    </ToggleRow>
);

const CookieConsent = () => {
    const {
        hydrated,
        showModal,
        functional,
        marketing,
        savePreferences,
        declineAll,
        closeSettings
    } = useConsent();

    const [functionalEnabled, setFunctionalEnabled] = useState(functional);
    const [marketingEnabled, setMarketingEnabled] = useState(marketing);

    const modalRef = useRef(null);
    const { setIsLocked } = useScrollLock(modalRef);

    const open = hydrated && showModal;

    // Sync local toggles with stored values whenever the modal (re)opens.
    useEffect(() => {
        if (open) {
            setFunctionalEnabled(functional);
            setMarketingEnabled(marketing);
        }
    }, [open, functional, marketing]);

    useEffect(() => {
        setIsLocked(open);
    }, [open, setIsLocked]);

    const data = useStaticQuery(graphql`
        query CookieConsentSettings {
            wp {
                siteSettings {
                    siteSettingsFields {
                        cookieConsentEnabled
                        cookieConsentText
                        cookieConsentNecessaryDescription
                        cookieConsentFunctionalDescription
                        cookieConsentMarketingDescription
                        privacyPage {
                            nodes {
                                ... on WpPage {
                                    uri
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const fields = data?.wp?.siteSettings?.siteSettingsFields || {};

    if (!fields.cookieConsentEnabled || !isBrowser() || !open) {
        return null;
    }

    const privacyUri = fields?.privacyPage?.node?.uri;

    const onEscapeKeyDown = (e) => {
        if (e.key === 'Escape') {
            closeSettings();
        }
    };

    return createPortal(
        <FocusLock returnFocus={true}>
            <Overlay
                ref={modalRef}
                onKeyDown={onEscapeKeyDown}
                onClick={(e) => {
                    if (e.target === e.currentTarget) closeSettings();
                }}
            >
                <Modal
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cookie-consent-title"
                >
                    <ModalHeader>
                        <ModalTitle id="cookie-consent-title">
                            Cookie Preferences
                        </ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                        {fields.cookieConsentText && (
                            <Description>
                                {fields.cookieConsentText}
                            </Description>
                        )}
                        <ToggleRows>
                            <Toggle
                                label="Necessary"
                                description={
                                    fields.cookieConsentNecessaryDescription
                                }
                                checked
                                disabled
                            />
                            <Toggle
                                label="Functional"
                                description={
                                    fields.cookieConsentFunctionalDescription
                                }
                                checked={functionalEnabled}
                                onToggle={() =>
                                    setFunctionalEnabled((prev) => !prev)
                                }
                            />
                            <Toggle
                                label="Marketing"
                                description={
                                    fields.cookieConsentMarketingDescription
                                }
                                checked={marketingEnabled}
                                onToggle={() =>
                                    setMarketingEnabled((prev) => !prev)
                                }
                            />
                        </ToggleRows>
                    </ModalBody>
                    <ModalFooter>
                        <FooterButtons>
                            <Button
                                buttonSize="small"
                                onClick={() =>
                                    savePreferences({
                                        functional: true,
                                        marketing: true
                                    })
                                }
                            >
                                Accept All
                            </Button>
                            <Button
                                buttonSize="small"
                                onClick={() =>
                                    savePreferences({
                                        functional: functionalEnabled,
                                        marketing: marketingEnabled
                                    })
                                }
                            >
                                Save Preferences
                            </Button>
                            <Button
                                buttonSize="small"
                                onClick={() => declineAll()}
                            >
                                Decline All
                            </Button>
                        </FooterButtons>
                        {privacyUri && (
                            <PolicyLink to={privacyUri} newTabOverride="newTab">
                                View Privacy Policy
                            </PolicyLink>
                        )}
                    </ModalFooter>
                </Modal>
            </Overlay>
        </FocusLock>,
        document.body
    );
};

export default CookieConsent;
